/**
 * Product (SKU) form. uc-core src/modules/pack/dto/pack.dto.ts CreateSkuDto { code ≤20 (stored upper
 * case), name ≤120, cropId, netG int ≥1, gtin? /^\d{8,14}$/ with a GS1 check digit (422 GTIN_INVALID,
 * stored as 14 digits), shelfLifeDays? int ≥1, toleranceG? /^\d+(\.\d)?$/, pouchesPerCarton? int ≥1 }.
 * UpdateSkuDto: name, gtin (null clears), shelfLifeDays, toleranceG, pouchesPerCarton — code, crop
 * and net weight never change (pack lots and labels carry them).
 */

export interface SkuDraft {
  code: string
  name: string
  cropId: string
  netG: string
  gtin: string
  shelfLifeDays: string
  toleranceG: string
  pouchesPerCarton: string
}

export type SkuProblems = Partial<Record<keyof SkuDraft, string>>

const CODE_RE = /^[A-Z0-9-]{1,20}$/
const INT_RE = /^\d{1,7}$/
const TOL_RE = /^\d+(\.\d)?$/

export const emptySkuDraft = (): SkuDraft => ({
  code: '',
  name: '',
  cropId: '',
  netG: '',
  gtin: '',
  shelfLifeDays: '',
  toleranceG: '',
  pouchesPerCarton: '',
})

export const normaliseSkuCode = (s: string) => s.toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '').slice(0, 20)

/** GS1 check digit (mod 10, weights 3/1 from the right, check digit excluded). */
export function gtinCheckDigitOk(g: string): boolean {
  if (!/^\d+$/.test(g) || g.length < 2) return false
  const digits = g.split('').map(Number)
  const check = digits.pop()!
  let sum = 0
  digits.reverse().forEach((d, i) => {
    sum += d * (i % 2 === 0 ? 3 : 1)
  })
  return (10 - (sum % 10)) % 10 === check
}

/** Same rule as uc-core normaliseGtin: 8, 12, 13 or 14 digits with a valid check digit. */
export function gtinOk(g: string): boolean {
  return [8, 12, 13, 14].includes(g.length) && gtinCheckDigitOk(g)
}

/** Stored GTINs come back as 14 digits; show them without the padding zeros of a shorter GTIN. */
export function displayGtin(g: string | null | undefined): string {
  if (!g) return ''
  if (g.length === 14 && g.startsWith('0')) {
    const t = g.replace(/^0+/, '')
    for (const len of [8, 12, 13]) if (t.length <= len) return t.padStart(len, '0')
  }
  return g
}

export interface SkuLike {
  code: string
  name: string
  cropId: string
  netG: number
  gtin: string | null
  shelfLifeDays: number | null
  toleranceG: string | null
  pouchesPerCarton: number | null
}

export function draftFromSku(s: SkuLike): SkuDraft {
  return {
    code: s.code,
    name: s.name,
    cropId: s.cropId,
    netG: String(s.netG),
    gtin: displayGtin(s.gtin),
    shelfLifeDays: s.shelfLifeDays === null ? '' : String(s.shelfLifeDays),
    toleranceG: s.toleranceG === null ? '' : String(Number(s.toleranceG)),
    pouchesPerCarton: s.pouchesPerCarton === null ? '' : String(s.pouchesPerCarton),
  }
}

const positiveInt = (v: string, what: string): string | undefined => {
  const t = v.trim()
  if (!INT_RE.test(t) || Number(t) < 1) return `${what}: a whole number of at least 1`
  return undefined
}

export function validateSkuDraft(d: SkuDraft, mode: 'create' | 'edit'): SkuProblems {
  const p: SkuProblems = {}
  if (mode === 'create') {
    if (!d.code) p.code = 'Enter a product code'
    else if (!CODE_RE.test(d.code)) p.code = 'Up to 20 capital letters, digits or dashes'
    if (!d.cropId) p.cropId = 'Choose the crop'
    if (!d.netG.trim()) p.netG = 'Enter the net weight per pouch'
    else p.netG = positiveInt(d.netG, 'Grams')
  }
  const name = d.name.trim()
  if (!name) p.name = 'Enter the product name'
  else if (name.length > 120) p.name = 'At most 120 characters'
  const gtin = d.gtin.trim()
  if (gtin) {
    if (!/^\d+$/.test(gtin) || ![8, 12, 13, 14].includes(gtin.length)) p.gtin = '8, 12, 13 or 14 digits'
    else if (!gtinCheckDigitOk(gtin)) p.gtin = 'The last digit (check digit) does not match — check the number'
  }
  if (d.shelfLifeDays.trim()) p.shelfLifeDays = positiveInt(d.shelfLifeDays, 'Days')
  if (d.pouchesPerCarton.trim()) p.pouchesPerCarton = positiveInt(d.pouchesPerCarton, 'Pouches')
  if (d.toleranceG.trim() && !TOL_RE.test(d.toleranceG.trim())) p.toleranceG = 'Grams with at most 1 decimal, e.g. 15 or 7.5'
  for (const k of Object.keys(p) as Array<keyof SkuDraft>) if (p[k] === undefined) delete p[k]
  return p
}

export const skuOk = (p: SkuProblems) => Object.keys(p).length === 0

const intOrUndef = (v: string) => (v.trim() ? Number(v.trim()) : undefined)

/** POST /skus body. Call only when valid. */
export function createSkuBody(d: SkuDraft) {
  const body: {
    code: string
    name: string
    cropId: string
    netG: number
    gtin?: string
    shelfLifeDays?: number
    toleranceG?: string
    pouchesPerCarton?: number
  } = { code: d.code, name: d.name.trim(), cropId: d.cropId, netG: Number(d.netG.trim()) }
  if (d.gtin.trim()) body.gtin = d.gtin.trim()
  const shelf = intOrUndef(d.shelfLifeDays)
  if (shelf !== undefined) body.shelfLifeDays = shelf
  if (d.toleranceG.trim()) body.toleranceG = d.toleranceG.trim()
  const ppc = intOrUndef(d.pouchesPerCarton)
  if (ppc !== undefined) body.pouchesPerCarton = ppc
  return body
}

/**
 * PATCH /skus/:id body. Call only when valid. An emptied GTIN is sent as null (uc-core clears it);
 * the other optional numbers cannot be cleared by uc-core, so an emptied one is left out.
 */
export function updateSkuBody(d: SkuDraft) {
  const body: {
    name: string
    gtin: string | null
    shelfLifeDays?: number
    toleranceG?: string
    pouchesPerCarton?: number
  } = { name: d.name.trim(), gtin: d.gtin.trim() || null }
  const shelf = intOrUndef(d.shelfLifeDays)
  if (shelf !== undefined) body.shelfLifeDays = shelf
  if (d.toleranceG.trim()) body.toleranceG = d.toleranceG.trim()
  const ppc = intOrUndef(d.pouchesPerCarton)
  if (ppc !== undefined) body.pouchesPerCarton = ppc
  return body
}
